import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { useBranchNav } from '../../hooks/branchNav.hook';
import '../dashboard/Dashboard.css';
import './BannersPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import IconButton from '../dashboard/components/product_item_components/IconButton';
import { TrashIcon } from '../dashboard/components/product_item_components/icons';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';

function bannerCountLabel(count) {
  if (count === 1) return 'баннер';
  if (count >= 2 && count <= 4) return 'баннера';
  return 'баннеров';
}

function BannersPage() {
  const navigate = useNavigate();
  const { branchHomePath } = useBranchNav();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [banners, setBanners] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [bannerToDelete, setBannerToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState(null);

  const fetchBanners = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta('/operator/banners', 'GET');
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить баннеры');
        setBanners([]);
        return;
      }
      const list = Array.isArray(res?.data?.banners) ? res.data.banners : [];
      setBanners(list);
    } catch (_) {
      setError('Ошибка загрузки');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  React.useEffect(() => {
    if (!bannerToDelete) setDeleteError(null);
  }, [bannerToDelete]);

  const handleBack = () => {
    navigate(branchHomePath);
  };

  const handleCreate = () => {
    navigate('/banners/create');
  };

  const handleConfirmDelete = async () => {
    const banner = bannerToDelete;
    const index = banner?.index;
    if (index == null) return;

    setDeleteError(null);
    const res = await requestWithMeta(
      `/operator/banners/${encodeURIComponent(index)}`,
      'DELETE'
    );
    if (!res?.ok) {
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить баннер');
      return;
    }

    const list = Array.isArray(res?.data?.banners) ? res.data.banners : null;
    if (list) setBanners(list);
    else setBanners((prev) => prev.filter((b) => b.index !== index));
    setBannerToDelete(null);
  };

  const deleteName =
    bannerToDelete != null ? `Баннер ${Number(bannerToDelete.index) + 1}` : '';

  return (
    <div className="banners-page-root">
      <CreateBusinessHeader
        title="Баннеры"
        subtitle=""
        onBack={handleBack}
      />

      <main className="banners-page-content">
        {loading ? (
          <div className="banners-page-loading" aria-busy="true">
            <div className="banners-page-skeleton" />
            <div className="banners-page-skeleton" />
            <div className="banners-page-skeleton" />
          </div>
        ) : error ? (
          <div className="banners-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader banners-page-sectionHeader">
              <button
                type="button"
                className="main-page-sectionSubButton"
                onClick={() => navigate('/banners/index')}
                disabled={banners.length === 0}
              >
                Порядок
              </button>
              <div className="main-page-sectionSub">
                <span>
                  {banners.length
                    ? `${banners.length} ${bannerCountLabel(banners.length)}`
                    : 'Нет баннеров'}
                </span>
              </div>
            </div>

            {banners.length === 0 ? (
              <div className="banners-page-empty">Нет баннеров</div>
            ) : (
              <ul className="banners-page-list" aria-label="Список баннеров">
                {banners.map((banner) => {
                  const index = banner?.index;
                  const url = String(banner?.url ?? '').trim();
                  const name = `Баннер ${Number(index) + 1}`;

                  return (
                    <li key={`${index}-${url}`} className="banners-page-item">
                      <div className="banners-page-itemRow">
                        <div className="banners-page-itemMain">
                          {url ? (
                            <img
                              className="banners-page-thumb"
                              src={url}
                              alt=""
                            />
                          ) : (
                            <div className="banners-page-thumb banners-page-thumb--empty" />
                          )}
                          <span className="banners-page-itemName">{name}</span>
                        </div>
                        <div className="banners-page-itemActions main-page-productActions">
                          <IconButton
                            onClick={() => setBannerToDelete(banner)}
                            label={`Удалить «${name}»`}
                            title="Удалить"
                            className="main-page-iconButton"
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="banners-page-footerRow">
        <button
          type="button"
          className="banners-page-footerButton"
          onClick={handleCreate}
        >
          + Добавить баннер
        </button>
      </footer>

      <DeleteProductModal
        isOpen={!!bannerToDelete}
        onClose={() => setBannerToDelete(null)}
        title="Удалить баннер"
        productName={deleteName}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default BannersPage;
